"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useOnboardingStepToast = void 0;
const onboarding_toast_1 = require("@app/component/core/onboarding/onboarding-toast");
const onboarding_1 = require("@app/reducer/onboarding");
const store_1 = require("@app/store");
const util_1 = require("@app/util");
const react_1 = __importDefault(require("react"));
const react_hot_toast_1 = require("react-hot-toast");
const react_router_dom_1 = require("react-router-dom");
/** where „Pokračovat" goes when the toast fired somewhere other than the checklist */
const CHECKLIST_PATH = '/onboarding';
/**
 * Turns „a step of the checklist has just become done" into the toast.
 *
 * Called from `OnboardingProvider`, which is mounted above the router — so this fires
 * wherever the manager happens to be, not only on the checklist. That is the point: „Vyřešit"
 * sends them to the step's own page, and the work is then finished there, by the assistant or
 * by their own hand.
 *
 * Two moments produce a toast. Work finished while the app is open, which arrives over the
 * websocket (see `use-onboarding-steps.ts`). And a step finished across a reload: that is what
 * `doneStepsAtStart` is for, since it records what the checklist looked like when „Vyřešit"
 * was clicked, so the celebration survives the page being reloaded in the middle of the work.
 *
 * Where there is no such marker the baseline is simply whatever was already done when the
 * watcher armed, which is what stops it toasting about steps finished days ago.
 */
const useOnboardingStepToast = ({ isLoading, steps }) => {
    const dispatch = (0, store_1.useRootDispatch)();
    const history = (0, react_router_dom_1.useHistory)();
    const { pathname } = (0, react_router_dom_1.useLocation)();
    const workspaceId = (0, store_1.useRootSelector)((state) => state.workspaceId);
    const activeStep = (0, store_1.useRootSelector)((state) => (0, util_1.isValue)(workspaceId) ? state.onboarding[workspaceId]?.activeStep : undefined);
    const doneStepsAtStart = (0, store_1.useRootSelector)((state) => (0, util_1.isValue)(workspaceId) ? state.onboarding[workspaceId]?.doneStepsAtStart : undefined);
    /** what „already done" means for this visit; unset until the first answer has landed */
    const baseline = react_1.default.useRef();
    // Setup steps only. The toast exists to carry news of work finished somewhere else — the
    // bonus is claimed by a click on the checklist itself, and the card answers on the spot by
    // replacing the button with the code. Celebrating that would drop a toast over the very
    // thing the click produced.
    const doneIds = steps
        .filter((step) => step.isDone && step.isReward !== true)
        .map((step) => step.id);
    // `steps` is rebuilt on every render, so the effect is keyed on the VALUE of what is
    // done — keying it on the array would re-run on every render of the page
    const doneKey = doneIds.join(',');
    react_1.default.useEffect(() => {
        if (isLoading)
            return;
        if (!(0, util_1.isDefined)(baseline.current)) {
            baseline.current = doneStepsAtStart ?? doneIds;
        }
        // everything finished since that baseline, not just one step: a single action can
        // finish two at once (publishing a schedule creates a shift and publishes it), and a
        // pip that quietly arrived already done while its neighbour animates is what reads as
        // „wrong order"
        const freshlyDoneIds = doneIds.filter((id) => baseline.current?.includes(id) !== true);
        if (freshlyDoneIds.length === 0)
            return;
        baseline.current = doneIds;
        // keyed by the steps it is about, so a second completion cannot stack a duplicate
        const toastId = `onboarding-step-${freshlyDoneIds.join('-')}`;
        // remove, not dismiss: dismiss only marks the toast invisible and leaves it mounted for
        // react-hot-toast's one-second exit window, and since this toast ignores `t.visible` it
        // would simply sit there fully drawn.
        const close = () => react_hot_toast_1.toast.remove(toastId);
        // On the checklist there is nowhere to go — it is right underneath the toast. Anywhere
        // else „Pokračovat" is the way back to it, to pick the next step. Only the button does
        // this; the countdown just closes (see `onExpire`), because the manager may well still
        // be working on the page the toast appeared over.
        const isOnChecklist = pathname.includes(CHECKLIST_PATH);
        react_hot_toast_1.toast.custom(() => (react_1.default.createElement(onboarding_toast_1.OnboardingToast, { pips: steps.map((step) => ({
                id: step.id,
                isDone: step.isDone,
                isReward: step.isReward === true,
            })), completedIds: freshlyDoneIds, onContinue: () => {
                close();
                if (!isOnChecklist)
                    history.push(CHECKLIST_PATH);
            }, onExpire: close })), { id: toastId, position: 'bottom-center', duration: Infinity });
        // the „what did I leave to do" marker has been spent; without clearing it, a remount
        // would replay the same celebration
        if ((0, util_1.isValue)(workspaceId) && (0, util_1.isValue)(activeStep)) {
            dispatch((0, onboarding_1.clearedOnboardingStep)(workspaceId));
        }
    }, [
        doneKey,
        isLoading,
        activeStep,
        doneStepsAtStart,
        workspaceId,
        pathname,
        history,
        dispatch,
    ]);
};
exports.useOnboardingStepToast = useOnboardingStepToast;
exports.default = exports.useOnboardingStepToast;
